// update-dependent-clis.ts
//
// Orchestrate check + auto-update các CLI phụ thuộc của Avatar:
//   Claude Code (npm), Supabase/git/gitleaks/trivy (brew), gitnexus (npm, hàm riêng).
//
// Gọi ở avatar init (autoUpdate=true) và avatar doctor (autoUpdate=false / --fix=true).
// Fail-soft tuyệt đối: KHÔNG throw, KHÔNG chặn init/doctor. 5 CLI chạy song song.

import { appendAuditEntry } from "./audit-log-appender.js";
import {
  type CliSpec,
  type CliUpdateAction,
  type CliUpdateResult,
  ensureCliLatest,
} from "./ensure-cli-latest.js";
import { ensureGitnexusInstalledAndLatest } from "./ensure-gitnexus-installed-and-latest.js";
import { log } from "./terminal-logger.js";

// gitnexus KHÔNG ở đây — đã có ensureGitnexusInstalledAndLatest (npm install nếu chưa
// cài + auto-update). Gọi riêng, adapt output về CliUpdateResult cho doctor render đồng nhất.
const DEPENDENT_CLIS: CliSpec[] = [
  {
    name: "Claude Code",
    bin: "claude",
    manager: "npm",
    pkg: "@anthropic-ai/claude-code",
    versionArgs: ["--version"],
  },
  {
    name: "Supabase",
    bin: "supabase",
    manager: "brew",
    pkg: "supabase",
    versionArgs: ["--version"],
  },
  { name: "git", bin: "git", manager: "brew", pkg: "git", versionArgs: ["--version"] },
  { name: "gitleaks", bin: "gitleaks", manager: "brew", pkg: "gitleaks", versionArgs: ["version"] },
  { name: "trivy", bin: "trivy", manager: "brew", pkg: "trivy", versionArgs: ["--version"] },
];

// gitnexus result shape khác → adapt về CliUpdateResult.
// ⚠ ensureGitnexusInstalledAndLatest TỰ update (autoUpdate ngầm) — gitnexus là core,
// luôn giữ mới nhất kể cả ở doctor (hành vi cũ từ trước, không đổi).
async function gitnexusAsCliResult(): Promise<CliUpdateResult> {
  try {
    const g = await ensureGitnexusInstalledAndLatest();
    if (!g.installed) {
      return {
        name: "GitNexus",
        installed: false,
        current: null,
        latest: null,
        action: "not-installed",
        detail: g.reason,
      };
    }
    return {
      name: "GitNexus",
      installed: true,
      current: g.version,
      latest: g.version,
      action: g.updated ? "updated" : g.reason === "update-failed" ? "update-failed" : "up-to-date",
      detail: g.reason,
    };
  } catch (err) {
    return {
      name: "GitNexus",
      installed: false,
      current: null,
      latest: null,
      action: "not-installed",
      detail: err instanceof Error ? err.message : String(err),
    };
  }
}

async function auditUpdate(r: CliUpdateResult): Promise<void> {
  if (r.action === "updated") {
    await appendAuditEntry("cli_update", `name=${r.name},to=${r.current ?? "?"},result=ok`);
  } else if (r.action === "update-failed") {
    await appendAuditEntry("cli_update", `name=${r.name},result=failed`);
  }
}

/**
 * Check (+ auto-update nếu opts.autoUpdate) toàn bộ CLI phụ thuộc.
 * Fail-soft: mỗi CLI đã tự nuốt lỗi → Promise.all không reject.
 */
export async function updateDependentClis(opts: {
  autoUpdate: boolean;
}): Promise<CliUpdateResult[]> {
  const cliResults = await Promise.all(
    DEPENDENT_CLIS.map((spec) =>
      ensureCliLatest(spec, { autoUpdate: opts.autoUpdate }).catch(
        (err): CliUpdateResult => ({
          name: spec.name,
          installed: false,
          current: null,
          latest: null,
          action: "not-installed",
          detail: err instanceof Error ? err.message : String(err),
        }),
      ),
    ),
  );

  // gitnexus tách (luôn auto-update — core tool). Chỉ chạy khi autoUpdate hoặc
  // doctor muốn báo version; vì nó tự update, chạy ở init là chính.
  const results = [...cliResults, await gitnexusAsCliResult()];

  for (const r of results) await auditUpdate(r);
  return results;
}

// Action nghĩa là CLI VẪN chưa khoẻ dù đã yêu cầu cập nhật.
const UNFIXED_AFTER_UPDATE: ReadonlySet<CliUpdateAction> = new Set([
  "update-available",
  "binary-broken",
  "update-failed",
]);

/**
 * Cho `doctor --fix`: ném lỗi nếu CLI `name` vẫn chưa được sửa sau
 * updateDependentClis({autoUpdate:true}). Env guard #213 (hạ về chỉ-kiểm) và cập nhật
 * thất bại đều KHÔNG throw (fail-soft) ⇒ không đọc lại kết quả thì applyFixes in
 * "Fixed" cho CLI vẫn cũ.
 */
export function assertDependentCliFixed(results: CliUpdateResult[], name: string): void {
  const r = results.find((x) => x.name === name);
  if (r && UNFIXED_AFTER_UPDATE.has(r.action)) {
    throw new Error(r.detail ?? `vẫn ${r.action} (v${r.current ?? "?"})`);
  }
}

/** Render gọn 1 dòng/CLI cho init (log.success/warn theo action). */
export function logCliResults(results: CliUpdateResult[]): void {
  for (const r of results) {
    if (r.action === "updated") {
      log.success(`  ✓ ${r.name}: cập nhật → v${r.current ?? "?"}`);
    } else if (r.action === "up-to-date") {
      log.dim(`  · ${r.name}: v${r.current ?? "?"} (mới nhất)`);
    } else if (r.action === "update-available") {
      log.info(`  ↑ ${r.name}: v${r.current} → v${r.latest} có bản mới`);
    } else if (r.action === "update-failed") {
      log.warn(`  ✗ ${r.name}: không cập nhật được (giữ v${r.current})`);
    } else if (r.action === "unmanaged") {
      // Không phải lỗi: binary đến từ nguồn khác (Apple Git, asdf, pyenv...).
      log.dim(`${r.name}: ${r.detail ?? "không do manager quản lý"}`);
    } else if (r.action === "skipped-no-manager") {
      log.dim(`  · ${r.name}: ${r.detail ?? "bỏ qua"}`);
    } else if (r.action === "not-installed") {
      log.dim(`  · ${r.name}: chưa cài`);
    } else if (r.action === "binary-broken") {
      log.warn(
        `  ✗ ${r.name}: cài dở (binary lỗi) — ${r.detail ?? "reinstall: npm install -g @anthropic-ai/claude-code"}`,
      );
    }
  }
}
